/**
 * sportNavigation — central navigation layer for the sport betting console.
 *
 * currentPage and currentParams are DERIVED getters (read-only view over
 * the authoritative domain stores). No state is duplicated here.
 * Only `history` is owned by this module.
 */

export const SPORT_PAGES = {
  HOME: 'home',
  TOURNAMENT: 'tournament',
  PREMATCH_DETAIL: 'prematch_detail',
  LIVE_LIST: 'live_list',
  LIVE_DETAIL: 'live_detail',
  /** @deprecated use LIVE_DETAIL */
  LIVE_DETAIL_PREMATCH: 'live_detail',
  PRELIVE_LIVE:         'prelive_live',
  PRELIVE_COMPETITIONS: 'prelive_competitions',
  PRELIVE_TOURNAMENT:   'prelive_tournament',
  /** The outright view (store/modules/antepost.module.js). */
  ANTEPOST:             'antepost',
}

/**
 * Slug / URL page of a tournament preview on its "Antepost" tab. Internally the page stays
 * TOURNAMENT (components key off it), with `antepostTab: true` in its params.
 */
export const SLUG_TOURNAMENT_ANTEPOST = 'tournament_antepost'

const state = {
  history: [],
}

const getters = {
  /**
   * Derives the current page from authoritative store state.
   * Priority: live-detail-in-prematch > prematch-detail > tournament > home
   */
  currentPage(state, getters, rootState, rootGetters) {
    const eventsLiveDetail = rootGetters['detailLive/getEventsLiveDetail'] || []
    const eventsDetail = rootGetters['detailPrematch/getEventsDetail'] || []
    const preview = rootGetters['previewPrematch/getPreview'] || []
    const viewMode = rootGetters['ui/getViewMode']

    // Live event detail open (from any context)
    if (eventsLiveDetail.length > 0) return SPORT_PAGES.LIVE_DETAIL

    // Prematch event detail open
    if (eventsDetail.length > 0) return SPORT_PAGES.PREMATCH_DETAIL

    // Outright view: it replaces the center column until something else opens
    if (rootGetters['antepost/isOpen']) return SPORT_PAGES.ANTEPOST

    // In the live section (viewMode === 'detail' with no event open)
    if (viewMode === 'detail') return SPORT_PAGES.LIVE_LIST

    // Tournament preview open in center (old-style previewPrematch)
    if (preview.length > 0) return SPORT_PAGES.TOURNAMENT

    // Prelive panel open (left column navigation)
    const openSportId = rootGetters['topsports/getOpenSportId']
    if (openSportId) {
      const mixedPreview = rootGetters['topsports/getMixedPreview']
      if (mixedPreview) return SPORT_PAGES.PRELIVE_TOURNAMENT
      const tab = (rootGetters['topsports/getTabBySport'] || {})[String(openSportId)]
      return tab === 'competitions' ? SPORT_PAGES.PRELIVE_COMPETITIONS : SPORT_PAGES.PRELIVE_LIVE
    }

    return SPORT_PAGES.HOME
  },

  /** Params of the currently active page, derived from domain stores. */
  currentParams(state, getters, rootState, rootGetters) {
    const page = getters.currentPage
    const eventsDetail = rootGetters['detailPrematch/getEventsDetail'] || []
    const eventsLiveDetail = rootGetters['detailLive/getEventsLiveDetail'] || []
    const preview = rootGetters['previewPrematch/getPreview'] || []

    if (page === SPORT_PAGES.PREMATCH_DETAIL && eventsDetail[0]) {
      const d = eventsDetail[0]
      const ev = d.event || {}
      const eventName = (ev.home && ev.away)
          ? `${ev.home} ${ev.away}`
          : (ev.competitorHome && ev.competitorAway ? `${ev.competitorHome} ${ev.competitorAway}` : null)
      return {
        eventId:       ev.id           || null,
        sportId:       d.sport         ? d.sport.id         : null,
        tournamentId:  d.tournament    ? d.tournament.id    : null,
        sportName:     d.sport         ? d.sport.name       : null,
        tournamentName: d.tournament   ? d.tournament.name  : null,
        eventName,
      }
    }
    if (page === SPORT_PAGES.LIVE_DETAIL && eventsLiveDetail[0]) {
      const d = eventsLiveDetail[0]
      const ev = d.event || {}
      const eventName = (ev.home && ev.away) ? `${ev.home} ${ev.away}` : null
      return {
        eventId:       ev.id           || null,
        sportId:       ev.sportId      || (d.sport ? d.sport.id : null),
        tournamentId:  ev.tournamentId || (d.tournament ? d.tournament.id : null),
        sportName:     d.sport         ? d.sport.name       : null,
        tournamentName: d.tournament   ? d.tournament.name  : null,
        eventName,
      }
    }
    if (page === SPORT_PAGES.TOURNAMENT && preview[0]) {
      const p = preview[0]
      const params = {
        tournamentId:  p.tournament ? p.tournament.id   : null,
        sportId:       p.sport      ? p.sport.id        : null,
        sportName:     p.sport      ? p.sport.name      : null,
        tournamentName: p.tournament ? p.tournament.name : null,
      }
      // On its Antepost tab: outright ids, the category to find the tournament and the season on screen.
      const tab = rootGetters['antepost/tournamentTab']
      if (tab && p.tournament && String(p.tournament.id) === String(tab.tournamentId)) {
        params.antepostTab = true
        params.categoryId  = tab.categoryId
        params.eventId     = tab.eventId
      }
      return params
    }

    if (page === SPORT_PAGES.ANTEPOST) {
      return { ...(rootGetters['antepost/navParams'] || {}) }
    }

    if (page === SPORT_PAGES.PRELIVE_TOURNAMENT) {
      const mp          = rootGetters['topsports/getMixedPreview']
      const openSportId = rootGetters['topsports/getOpenSportId']
      return {
        sportId:       openSportId      ? String(openSportId)           : null,
        categoryId:    mp?.category?.id  ? String(mp.category.id)        : null,
        tournamentId:  mp?.tournament?.id ? String(mp.tournament.id)     : null,
        sportName:     mp?.sport?.name      || null,
        tournamentName: mp?.tournament?.name || null,
      }
    }
    if (page === SPORT_PAGES.PRELIVE_COMPETITIONS) {
      const openSportId    = rootGetters['topsports/getOpenSportId']
      const expandedCatIds = rootGetters['topsports/getExpandedCatIds'] || []
      return {
        sportId:    openSportId ? String(openSportId) : null,
        // Last opened accordion is the last item in the array
        categoryId: expandedCatIds.length > 0 ? String(expandedCatIds[expandedCatIds.length - 1]) : null,
      }
    }
    if (page === SPORT_PAGES.PRELIVE_LIVE) {
      const openSportId = rootGetters['topsports/getOpenSportId']
      return { sportId: openSportId ? String(openSportId) : null }
    }

    return {}
  },

  getHistory: state => state.history,
  getPreviousPage: state => state.history.length > 0 ? state.history[state.history.length - 1] : null,
}

const mutations = {
  PUSH_HISTORY(state, entry) {
    state.history = [...state.history, entry]
  },
  POP_HISTORY(state) {
    const h = [...state.history]
    h.pop()
    state.history = h
  },
  CLEAR_HISTORY(state) {
    state.history = []
  },
}

const actions = {
  /** Snapshot current page into history before navigating.
   *  De-duplicates: se la pagina+params sono identici all'ultimo entry, non ripete il push.
   *  Questo evita double-push quando sia un componente sia l'azione Vuex chiamano pushHistory. */
  pushHistory({ commit, getters, state }) {
    const entry = { page: getters.currentPage, params: getters.currentParams }
    const last = state.history[state.history.length - 1]
    if (last && last.page === entry.page &&
        JSON.stringify(last.params) === JSON.stringify(entry.params)) {
      return
    }
    commit('PUSH_HISTORY', entry)
  },

  /** Pop the last history entry without any side-effects (used for in-panel back navigation). */
  popHistory({ commit }) {
    commit('POP_HISTORY')
  },

  /** Reset the navigation history (e.g. when opening a new sport). */
  clearHistory({ commit }) {
    commit('CLEAR_HISTORY')
  },

  /** Close current content and go back to the previous history entry. */
  async goBack({ state, commit, getters, dispatch, rootGetters }) {
    if (state.history.length === 0) {
      // Nessuna history: chiude comunque dettagli e preview aperti per garantire
      // che il browser back lasci l'iframe in uno stato pulito anche senza entry.
      const eventsDetail     = rootGetters['detailPrematch/getEventsDetail']  || []
      const eventsLiveDetail = rootGetters['detailLive/getEventsLiveDetail']  || []
      // viewMode prima della chiusura: evita il passaggio transitorio per live_list
      await dispatch('ui/setViewMode', { mode: 'preview', skipHistory: true }, { root: true })
      for (const d of eventsLiveDetail) {
        const id = (d.event && d.event.id) || d.source
        if (id) await dispatch('detailLive/removeEventLiveDetail', id, { root: true })
      }
      for (const d of eventsDetail) {
        if (d.source) await dispatch('detailPrematch/removeFromDetail', { source: d.source, type: d.type }, { root: true })
      }
      await dispatch('topsports/closePanel', null, { root: true })
      await dispatch('antepost/close', null, { root: true })
      return
    }

    // targetEntry = la pagina a cui stiamo tornando (l'ultimo elemento PRIMA del pop)
    const targetEntry = state.history[state.history.length - 1]
    const targetPage  = targetEntry ? targetEntry.page : SPORT_PAGES.HOME
    commit('POP_HISTORY')

    const PRELIVE_PAGES = [SPORT_PAGES.PRELIVE_LIVE, SPORT_PAGES.PRELIVE_COMPETITIONS, SPORT_PAGES.PRELIVE_TOURNAMENT]
    const currentPage   = getters.currentPage

    // ── Back dentro/fuori dalla vista antepost ─────────────────────────────
    // Opening the view closed what was under it, so leaving it re-opens the target from its params.
    if (currentPage === SPORT_PAGES.ANTEPOST) {
      if (targetPage === SPORT_PAGES.ANTEPOST) {
        await dispatch('antepost/restore', targetEntry.params, { root: true })
        return
      }
      await dispatch('antepost/close', null, { root: true })
      await dispatch('reopenEntry', targetEntry)
      return
    }

    // ── Back dentro/fuori dal pannello prelive ─────────────────────────────
    // Replica esattamente la logica di PreliveSection.closePanel:
    //  - target prelive_competitions : clearMixedPreview + setTab('competitions')
    //  - target prelive_live         : clearMixedPreview + setTab('liveandnext')
    //  - target prelive_tournament   : solo clearMixedPreview (non cambia tab)
    //  - target non-prelive          : closePanel (azzera openSportId)
    if (PRELIVE_PAGES.includes(currentPage)) {
      if (PRELIVE_PAGES.includes(targetPage)) {
        const sportId = rootGetters['topsports/getOpenSportId']
        await dispatch('topsports/clearMixedPreview', null, { root: true })
        if (targetPage === SPORT_PAGES.PRELIVE_COMPETITIONS) {
          await dispatch('topsports/setTabForSport', { sportId, tab: 'competitions' }, { root: true })
        } else if (targetPage === SPORT_PAGES.PRELIVE_LIVE) {
          // Never restore the 'liveandnext' tab when live is disabled at the platform level —
          // it's filtered out of the toolbar, so the user would end up on a hidden tab.
          const liveEnabled = rootGetters['cart/getSettings']?.enableLive !== false
          const targetTab = liveEnabled ? 'liveandnext' : 'competitions'
          await dispatch('topsports/setTabForSport', { sportId, tab: targetTab }, { root: true })
        }
      } else {
        // Esco dal pannello prelive: chiudo completamente
        await dispatch('topsports/closePanel', null, { root: true })
        if (targetPage === SPORT_PAGES.ANTEPOST) {
          await dispatch('antepost/open', { ...targetEntry.params, skipHistory: true }, { root: true })
        }
      }
      return
    }

    // ── Back da sezioni non-prelive ────────────────────────────────────────
    const eventsDetail     = rootGetters['detailPrematch/getEventsDetail']    || []
    const eventsLiveDetail = rootGetters['detailLive/getEventsLiveDetail']    || []
    const preview          = rootGetters['previewPrematch/getPreview']        || []

    // Ripristina il viewMode corretto PRIMA di chiudere i dettagli: un dettaglio live
    // aperto dalla home ha viewMode='detail', e chiuderlo prima del cambio di viewMode
    // farebbe passare transitoriamente per live_list (flicker di pagina e di URL).
    if (targetPage === SPORT_PAGES.LIVE_LIST) {
      await dispatch('ui/setViewMode', { mode: 'detail', skipHistory: true }, { root: true })
    } else if (
        targetPage === SPORT_PAGES.HOME ||
        targetPage === SPORT_PAGES.TOURNAMENT ||
        targetPage === SPORT_PAGES.PRELIVE_LIVE ||
        targetPage === SPORT_PAGES.PRELIVE_COMPETITIONS ||
        targetPage === SPORT_PAGES.PRELIVE_TOURNAMENT
    ) {
      await dispatch('ui/setViewMode', { mode: 'preview', skipHistory: true }, { root: true })
    }

    // Chiude il dettaglio live aperto
    for (const d of eventsLiveDetail) {
      const id = (d.event && d.event.id) || d.source
      if (id) await dispatch('detailLive/removeEventLiveDetail', id, { root: true })
    }
    // Chiude il dettaglio prematch aperto
    for (const d of eventsDetail) {
      if (d.source) await dispatch('detailPrematch/removeFromDetail', { source: d.source, type: d.type }, { root: true })
    }

    // Se torniamo a home o live_list, chiudiamo anche il tournament preview
    if (targetPage === SPORT_PAGES.HOME || targetPage === SPORT_PAGES.LIVE_LIST) {
      for (const p of preview) {
        if (p.source) await dispatch('previewPrematch/removeFromPreview', { source: p.source }, { root: true })
      }
      await dispatch('topsports/closePanel', null, { root: true })

    }

    // Detail opened from the outright view: back to the view, on the same event / sport
    if (targetPage === SPORT_PAGES.ANTEPOST) {
      await dispatch('antepost/open', { ...targetEntry.params, skipHistory: true }, { root: true })
    }
    // Tournament preview: back restores its tab (match odds / outright, and the season)
    if (targetPage === SPORT_PAGES.TOURNAMENT) {
      if (targetEntry.params && targetEntry.params.antepostTab) {
        await dispatch('antepost/showTournamentTab', { ...targetEntry.params, skipHistory: true }, { root: true })
      } else {
        await dispatch('antepost/hideTournamentTab', { skipHistory: true }, { root: true })
      }
    }
  },

  /**
   * Re-opens a history entry after leaving the outright view, which had closed it.
   * Same calls as the slug restore in TemplateMarketView._handleSlugNavigation.
   */
  async reopenEntry({ dispatch }, entry) {
    const page = entry ? entry.page : SPORT_PAGES.HOME
    const p = (entry && entry.params) || {}

    if (page === SPORT_PAGES.LIVE_LIST) {
      await dispatch('ui/setViewMode', { mode: 'detail', section: 'live', skipHistory: true }, { root: true })
      return
    }
    await dispatch('ui/setViewMode', { mode: 'preview', skipHistory: true }, { root: true })

    if (page === SPORT_PAGES.LIVE_DETAIL && p.eventId) {
      await dispatch('detailLive/fetchEventLiveDetailV5', {
        source: p.eventId, sport: p.sportId, tournament: p.tournamentId, group: 0,
        eventName: p.eventName || '', skipHistory: true,
      }, { root: true })
    } else if (page === SPORT_PAGES.PREMATCH_DETAIL && p.eventId) {
      await dispatch('detailPrematch/fetchEventDetailV3', {
        source: p.eventId, sport: p.sportId, tournament: p.tournamentId,
        competitorHome: p.eventName || '', competitorAway: '', eventName: p.eventName || '',
        group: null, skipHistory: true,
      }, { root: true })
    } else if (page === SPORT_PAGES.TOURNAMENT && p.tournamentId && p.sportId) {
      if (p.antepostTab) {
        await dispatch('antepost/showTournamentTab', { ...p, skipHistory: true }, { root: true })
      }
      const { checkIsMobileDevice, checkIsTabletDevice } = await import('@/plugins/device-inspector')
      await dispatch('previewPrematch/fetchPreviewV3', {
        source: p.tournamentId + '#prematch',
        tournament: p.tournamentId,
        type: 'previewOdds',
        sportId: p.sportId,
        sportName: p.sportName || '',
        tournamentName: p.tournamentName || '',
        groupOddsWithSpreads: true,
        forceDefaultGameConsole: false,
        mobilePlatform: checkIsMobileDevice() || checkIsTabletDevice(),
        skipHistory: true,
      }, { root: true })
    }
  },

  /**
   * Close a prematch tournament re-clicked in the left sidebar (sport tree, favourites,
   * popular): a second click on the selected row toggles it off.
   *  - Prematch event detail open on top: drop only the detail, so the tournament under it
   *    shows again. Removing a preview hidden under the detail would look like a no-op.
   *  - Otherwise: remove it from the preview, same as the X on its card, and clear the
   *    prelive panel's mixed preview when that is what is showing it.
   */
  async closeTournament({ dispatch, rootGetters }, { tournamentId }) {
    const eventsDetail = rootGetters['detailPrematch/getEventsDetail'] || []
    if (eventsDetail.length > 0) {
      await dispatch('detailPrematch/clearAllDetail', null, { root: true })
      return
    }

    const tid = String(tournamentId)
    // Same two source spellings the sidebar's "is selected" check accepts. Both lists are
    // scanned: the classic sidebar reads tournamentsActive, American and Terminal read the
    // preview, and the American popular row resets the preview without touching the other.
    const matches = source => String(source) === tid || String(source) === tid + '#prematch'
    const sources = new Set([
      ...(rootGetters['previewPrematch/getTournamentsActive'] || []),
      ...(rootGetters['previewPrematch/getPreview'] || []).map(p => p.source),
    ].filter(matches))
    for (const source of sources) {
      await dispatch('previewPrematch/removeFromPreview', { source }, { root: true })
    }

    const mixedPreview = rootGetters['topsports/getMixedPreview']
    if (mixedPreview && mixedPreview.tournament && String(mixedPreview.tournament.id) === tid) {
      await dispatch('topsports/clearMixedPreview', null, { root: true })
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
